import React from "react";
import {
    Box,
    Card,
    CardActionArea,
    CardContent,
    Typography,
    Chip,
    IconButton,
    Tooltip,
    Divider,
    CircularProgress,
} from "@mui/material";
import {
    Visibility as ViewIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    FolderSpecial as ProjectIcon,
    AttachFile as AttachFileIcon,
} from "@mui/icons-material";
import { format, parseISO } from "date-fns";

function formatDate(dateStr) {
    if (!dateStr) return "—";
    try {
        return format(parseISO(dateStr), "MM/dd/yyyy");
    } catch {
        return dateStr;
    }
}

function linkedLabel(report) {
    if (report.project_detail) {
        return `${report.project_detail.number} · ${report.project_detail.name}`;
    }
    if (report.quote_detail) {
        return `${report.quote_detail.number} · ${report.quote_detail.name}`;
    }
    return "—";
}

export default function ProjectReportsList({
    reports,
    loading,
    onView,
    onEdit,
    onDelete,
}) {
    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (reports.length === 0) {
        return (
            <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                <ProjectIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
                <Typography variant="body2">
                    No Project Reports found.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {reports.map((report) => (
                <Card
                    key={report.id}
                    sx={{
                        boxShadow: "none",
                        border: 1,
                        borderColor: "primary.main",
                    }}
                >
                    <CardActionArea onClick={() => onView(report)}>
                        <CardContent
                            sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}
                        >
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    variant="subtitle2"
                                    fontWeight={700}
                                    sx={{ fontFamily: "Montserrat, sans-serif" }}
                                >
                                    {report.number}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    noWrap
                                >
                                    {linkedLabel(report)}
                                </Typography>
                                {report.comments && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            mt: 0.5,
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                        }}
                                    >
                                        {report.comments}
                                    </Typography>
                                )}
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                        mt: 0.75,
                                        flexWrap: "wrap",
                                    }}
                                >
                                    <Chip
                                        label={formatDate(report.report_date)}
                                        size="small"
                                        color="secondary"
                                    />
                                    {report.attachments_detail?.length > 0 && (
                                        <Chip
                                            icon={<AttachFileIcon />}
                                            label={report.attachments_detail.length}
                                            size="small"
                                            variant="outlined"
                                        />
                                    )}
                                </Box>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    sx={{ mt: 0.5 }}
                                >
                                    {formatDate(report.created)}
                                    {report.created_by_detail
                                        ? ` · ${report.created_by_detail.full_name || report.created_by_detail.username}`
                                        : ""}
                                </Typography>
                            </Box>
                        </CardContent>
                    </CardActionArea>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            px: 1,
                            pb: 1,
                            gap: 0.5,
                        }}
                    >
                        <Tooltip title="View">
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={() => onView(report)}
                            >
                                <ViewIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
                        <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => onEdit(report)}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
                        <Tooltip title="Delete">
                            <IconButton
                                size="small"
                                color="error"
                                onClick={() => onDelete(report)}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Card>
            ))}
        </Box>
    );
}
